import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useMatch, useLocation } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Collapse,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PageTitleIcon from '../components/PageTitleIcon';
import { CONTENT_FONT_FAMILY_VAR } from '../constants/fonts';
import { useAuthStore } from '../stores/auth.store';
import { replaceUserCollectionSites as replaceUserCollectionSitesRequest } from '../services/auth.service';
import { getCollectionSites } from '../services/collection-site.service';
import { CollectionSiteSchema } from '../types/collection-site.type';
import { RegisterRequest, UserSchema } from '../types/auth.type';
import { useOrganizationStore } from '../stores/organization.store';
import { OrganizationType, isSiteOperationsOrgType } from '../types/organization.type';
import { useAuthPermissions } from '../hooks/useAuthPermissions';
import {
  type CanonicalRole,
  type RoleOrgType,
  isRoleOrgType,
  legacyRoleKey,
  inferRoleLabel,
  inferCanonicalRole,
  canGrantRolePreset,
  assertCanGrantAll,
  mergeFlatPermissionsForOrgSave,
  permissionsChanged,
  flatPermissionsFromUser,
  expandRoleForSites,
  canonicalRoleOptions,
  expandPermissionsForCollectionSites,
  normalizePermissionsForEditor,
} from '../auth';
import AdvancedPermissionsEditor from '../components/users/AdvancedPermissionsEditor';
import PasswordSetupSection from '../components/users/PasswordSetupSection';
import PermissionsReferenceDialog from '../components/users/PermissionsReferenceDialog';
import { SetupCoachmark } from '../components/selfSetup/SetupCoachmark';
import { HELP_GUIDE_ADD_USER } from '../constants/knowledgeBaseTryIt';
import {
  clearHelpGuideActive,
  markHelpGuideActive,
} from '../utils/helpGuideSession';
import { isPasswordValid } from '../utils/password.helpers';
import {
  applySitesEntryToBranchState,
  collectionSiteLabel,
  EMPLOYEE_LEVEL_OPTIONS,
  formDataDefaults,
  GENDER_OPTIONS,
  isValidEmail,
  isValidSaudiPhone,
  NATIONALITY_OPTIONS,
  UserFormData,
} from '../utils/users.helpers';
import { useTranslation } from 'react-i18next';

interface SectionCardProps {
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  headerAction?: React.ReactNode;
  children: React.ReactNode;
}

function SectionCard({ icon, title, subtitle, headerAction, children }: SectionCardProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Card
      elevation={0}
      sx={{
        mb: 2.5,
        borderRadius: '16px',
        border: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.22 : 0.12)}`,
        bgcolor: theme.palette.background.paper,
        boxShadow: isDark
          ? `0 8px 24px ${alpha('#000', 0.28)}`
          : `0 8px 24px ${alpha('#0f172a', 0.05)}`,
        overflow: 'hidden',
      }}
    >
      <CardContent sx={{ p: { xs: 2.25, sm: 3 }, '&:last-child': { pb: { xs: 2.25, sm: 3 } } }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'flex-start' }}
          spacing={1.5}
          sx={{
            mb: 2.5,
            pb: 2,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.9)}`,
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ flex: 1 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                color: theme.palette.primary.main,
                flexShrink: 0,
              }}
            >
              {icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontFamily: CONTENT_FONT_FAMILY_VAR,
                  fontWeight: 700,
                  fontSize: '1.05rem',
                  lineHeight: 1.3,
                  color: 'text.primary',
                }}
              >
                {title}
              </Typography>
              {subtitle && (
                <Typography
                  sx={{
                    fontFamily: CONTENT_FONT_FAMILY_VAR,
                    mt: 0.35,
                    fontSize: '0.875rem',
                    lineHeight: 1.45,
                    color: 'text.secondary',
                  }}
                >
                  {subtitle}
                </Typography>
              )}
            </Box>
          </Stack>
          {headerAction}
        </Stack>
        {children}
      </CardContent>
    </Card>
  );
}

const UserEdit: React.FC = () => {
  const { t } = useTranslation();
  const { userId } = useParams<{ userId: string }>();
  const isNewRoute = useMatch({ path: '/users/new', end: true });
  const isNew = Boolean(isNewRoute);
  const navigate = useNavigate();
  const location = useLocation();

  const { getOrganizationUsers, register, updateOrganizationUser } = useAuthStore();
  const { selectedOrganization } = useOrganizationStore();
  const {
    isReadOnly,
    isPlatformAdmin,
    permissionList,
    tokenPayload,
    canManageTeam,
    canAssignSites,
  } = useAuthPermissions();
  const [helpAddGuide, setHelpAddGuide] = useState(false);
  /** Knowledge Base "Go to Add User" guided tips. */
  const showUserFormTour = isNew && helpAddGuide;

  useEffect(() => {
    if (!isNew) return;
    const guide = new URLSearchParams(location.search).get('helpGuide');
    if (guide !== HELP_GUIDE_ADD_USER) return;
    markHelpGuideActive(guide);
    setHelpAddGuide(true);
    navigate({ pathname: location.pathname, search: '' }, { replace: true });
  }, [isNew, location.search, location.pathname, navigate]);

  type UserFormTourPhase =
    | 'account'
    | 'password'
    | 'personal'
    | 'role'
    | 'save';

  const formTourPhases = useMemo((): UserFormTourPhase[] => {
    const phases: UserFormTourPhase[] = ['account'];
    if (isNew) phases.push('password');
    phases.push('personal', 'role', 'save');
    return phases;
  }, [isNew]);

  const [formTourIdx, setFormTourIdx] = useState(0);

  useEffect(() => {
    if (showUserFormTour) setFormTourIdx(0);
  }, [showUserFormTour]);

  const formTourPhase = showUserFormTour
    ? formTourPhases[formTourIdx] ?? null
    : null;

  useEffect(() => {
    if (!formTourPhase) return;
    const id = `self-setup-form-${formTourPhase}`;
    window.requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    });
  }, [formTourPhase]);

  const finishTeamTour = () => {
    setHelpAddGuide(false);
    clearHelpGuideActive();
  };

  const advanceFormTour = () => {
    if (formTourIdx >= formTourPhases.length - 1) {
      finishTeamTour();
      return;
    }
    setFormTourIdx((i) => i + 1);
  };

  const orgType = selectedOrganization?.organization_type;
  const roleOrgType: RoleOrgType | null =
    orgType && isRoleOrgType(orgType) ? orgType : null;
  const isSiteOpsOrg = orgType ? isSiteOperationsOrgType(orgType) : false;
  const isTruckOrg = orgType === OrganizationType.TRUCK;
  const orgId = selectedOrganization?.id;

  const [loading, setLoading] = useState(!isNew);
  const [selectedUser, setSelectedUser] = useState<UserSchema | null>(null);
  const [formData, setFormData] = useState<UserFormData>(formDataDefaults);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string }>({
    open: false,
    message: '',
  });

  const [selectedCanonicalRole, setSelectedCanonicalRole] = useState<CanonicalRole | ''>('');
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [advancedPermissions, setAdvancedPermissions] = useState<string[]>([]);

  const [branchSitesList, setBranchSitesList] = useState<CollectionSiteSchema[]>([]);
  const [branchSitesLoading, setBranchSitesLoading] = useState(false);
  const [allBranchSites, setAllBranchSites] = useState(false);
  const [selectedBranchSiteIds, setSelectedBranchSiteIds] = useState<string[]>([]);
  const [siteSearch, setSiteSearch] = useState('');
  const [permissionsRefOpen, setPermissionsRefOpen] = useState(false);

  const canWrite = orgId ? canManageTeam(orgId) && !isReadOnly : false;
  const canEditSites = orgId ? canAssignSites(orgId) : false;

  const siteIdsForRole = useMemo((): string[] | '*' => {
    if (!isSiteOpsOrg) return '*';
    if (allBranchSites) return '*';
    return selectedBranchSiteIds;
  }, [isSiteOpsOrg, allBranchSites, selectedBranchSiteIds]);

  const grantablePresetRoles = useMemo(() => {
    if (!roleOrgType || !orgId) return [];
    return canonicalRoleOptions().filter(({ value }) =>
      canGrantRolePreset(
        permissionList,
        tokenPayload,
        roleOrgType,
        orgId,
        value,
        siteIdsForRole,
      ),
    );
  }, [roleOrgType, orgId, permissionList, tokenPayload, siteIdsForRole]);

  useEffect(() => {
    if (isNew) {
      // AuthRoute already requires write permission; allow org admins (not only
      // platform admins) to open the add-user form.
      if (!orgId) return;
      if (!canManageTeam(orgId) || isReadOnly) {
        navigate('/users', { replace: true });
      }
      return;
    }
    if (!orgId || !userId) return;

    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const users = await getOrganizationUsers(orgId);
        const found = users.find((u) => (u.id ?? u.sub) === userId);
        if (cancelled) return;
        if (!found) {
          navigate('/users', { replace: true });
          return;
        }
        setSelectedUser(found);
        setFormData({
          username: found.username,
          password: '',
          password_confirm: '',
          phone: found.phone ?? '',
          first_name: found.data?.first_name ?? '',
          last_name: found.data?.last_name ?? '',
          date_of_birth: found.data?.date_of_birth ?? '',
          gender: found.data?.gender ?? '',
          nationality: found.data?.nationality ?? '',
          status: (found.status === 'inactive' ? 'inactive' : 'active') as 'active' | 'inactive',
          employee_level: (found.data?.employee_level as string) ?? '',
          iban: found.data?.iban ?? '',
          driver_license_expiry: found.data?.driver_license_expiry ?? '',
          driver_license_number: found.data?.driver_license_number ?? '',
          vehicle_registration_number: found.data?.vehicle_registration_number ?? '',
        });
        if (roleOrgType) {
          const siteEntry = found.data?.sites?.[orgId];
          const { all, ids } = applySitesEntryToBranchState(siteEntry);
          setAllBranchSites(all);
          setSelectedBranchSiteIds(ids);

          const canonical = inferCanonicalRole(found, orgId, roleOrgType);
          const siteScope = all ? '*' : ids;
          if (canonical === 'custom') {
            setAdvancedOpen(true);
            const flat = flatPermissionsFromUser(found);
            setAdvancedPermissions(
              isSiteOpsOrg
                ? normalizePermissionsForEditor(flat, orgId, siteScope)
                : flat,
            );
          } else if (canonical !== 'unknown') {
            setSelectedCanonicalRole(canonical);
          }
        }
      } catch {
        if (!cancelled) setError(t('users.form.errors.failedLoad'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    isNew,
    orgId,
    userId,
    canManageTeam,
    isReadOnly,
    navigate,
    getOrganizationUsers,
    roleOrgType,
    isSiteOpsOrg,
  ]);

  useEffect(() => {
    if (!orgId || !isSiteOpsOrg) {
      setBranchSitesList([]);
      return;
    }
    let cancelled = false;
    setBranchSitesLoading(true);
    (async () => {
      try {
        const response = await getCollectionSites({ organization_id: orgId });
        const data = response as { result?: CollectionSiteSchema[] };
        if (!cancelled) {
          setBranchSitesList(Array.isArray(data.result) ? data.result : []);
        }
      } catch {
        if (!cancelled) setBranchSitesList([]);
      } finally {
        if (!cancelled) setBranchSitesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId, isSiteOpsOrg]);

  const handleSubmit = async () => {
    setError(null);

    if (!orgId || !roleOrgType) {
      setError(t('users.form.errors.selectOrganization'));
      return;
    }
    if (!canWrite) {
      setError(t('users.form.errors.noPermission'));
      return;
    }
    if (!isValidEmail(formData.username.trim())) {
      setError(t('users.form.errors.invalidEmail'));
      return;
    }
    if (formData.phone && formData.phone.trim() && !isValidSaudiPhone(formData.phone)) {
      setError(t('users.form.errors.invalidPhone'));
      return;
    }
    if (isNew) {
      if (!isPasswordValid(formData.password)) {
        setError(t('users.form.passwordRequirements'));
        return;
      }
      if (formData.password !== formData.password_confirm) {
        setError(t('users.form.errors.passwordsMismatch'));
        return;
      }
    }

    const usingAdvanced = advancedOpen && advancedPermissions.length > 0;
    if (!usingAdvanced && !selectedCanonicalRole) {
      setError(t('users.form.errors.selectRole'));
      return;
    }
    if (
      isSiteOpsOrg &&
      !usingAdvanced &&
      (selectedCanonicalRole === 'ops_manager' || selectedCanonicalRole === 'staff') &&
      !allBranchSites &&
      selectedBranchSiteIds.length === 0
    ) {
      setError(t('users.form.errors.assignSite'));
      return;
    }
    if (!formData.gender?.trim()) {
      setError(t('users.form.errors.selectGender'));
      return;
    }

    setIsSubmitting(true);
    const priorPermissions = selectedUser?.data?.permissions;

    const putSitesForUser = async (targetUserId: string): Promise<boolean> => {
      if (!isSiteOpsOrg) return true;
      const payload = allBranchSites ? ['*'] : selectedBranchSiteIds;
      try {
        const res = (await replaceUserCollectionSitesRequest(orgId, targetUserId, payload)) as {
          result?: UserSchema;
        };
        return res.result != null;
      } catch {
        return false;
      }
    };

    try {
      const dataFields = {
        first_name: formData.first_name || null,
        last_name: formData.last_name || null,
        gender: formData.gender?.trim() || null,
        nationality: formData.nationality?.trim() || null,
        date_of_birth: formData.date_of_birth?.trim() || null,
        employee_level: (formData.employee_level as 'Senior' | 'Mid' | 'Line' | null) || null,
        iban: formData.iban?.trim() || null,
        driver_license_expiry: isTruckOrg ? formData.driver_license_expiry?.trim() || null : null,
        driver_license_number: isTruckOrg ? formData.driver_license_number?.trim() || null : null,
        vehicle_registration_number: isTruckOrg
          ? formData.vehicle_registration_number?.trim() || null
          : null,
      };

      let permissionsPayload: string[] | Record<string, string[]>;
      if (usingAdvanced) {
        const existing = flatPermissionsFromUser(selectedUser ?? { data: {} });
        const scopedPermissions = isSiteOpsOrg
          ? expandPermissionsForCollectionSites(advancedPermissions, orgId, siteIdsForRole)
          : advancedPermissions;
        assertCanGrantAll(permissionList, scopedPermissions, tokenPayload);
        permissionsPayload = mergeFlatPermissionsForOrgSave(existing, orgId, scopedPermissions);
      } else {
        const role = selectedCanonicalRole as CanonicalRole;
        const expanded = expandRoleForSites(roleOrgType, role, orgId, siteIdsForRole);
        assertCanGrantAll(permissionList, expanded, tokenPayload);
        permissionsPayload = { [orgId]: [legacyRoleKey(roleOrgType, role)] };
      }

      const permissionsDidChange = !isNew
        ? permissionsChanged(priorPermissions, permissionsPayload)
        : true;

      if (!isNew && selectedUser) {
        const targetId = selectedUser.id ?? selectedUser.sub;
        if (!targetId) throw new Error('User id not found');
        const updated = await updateOrganizationUser(orgId, targetId, {
          username: formData.username.trim(),
          phone: formData.phone?.trim() || null,
          ...(isPlatformAdmin ? { status: formData.status } : {}),
          data: { permissions: permissionsPayload, ...dataFields },
        });
        if (!updated) throw new Error('Failed to save');
        if (isSiteOpsOrg && canEditSites) {
          await putSitesForUser(targetId);
        }
        navigate('/users', {
          state: {
            snackbar: permissionsDidChange
              ? t('users.form.success.updatedRelogin')
              : t('users.form.success.updated'),
          },
        });
      } else {
        const newUser: RegisterRequest = {
          username: formData.username.trim(),
          password: formData.password,
          password_confirm: formData.password_confirm,
          phone: formData.phone?.trim() || null,
          data: { permissions: permissionsPayload, ...dataFields },
        };
        const created = await register(newUser);
        if (!created) throw new Error('Failed to create user');
        const newId = created.id ?? created.sub;
        if (isSiteOpsOrg && canEditSites && newId) {
          await putSitesForUser(newId);
        }
        if (showUserFormTour) {
          finishTeamTour();
        }
        navigate('/users', { state: { snackbar: t('users.form.success.created') } });
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : '';
      setError(
        msg.includes('Cannot grant')
          ? t('users.form.errors.cannotGrant')
          : t('users.form.errors.failedSave'),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!orgId || !roleOrgType) {
    return (
      <Box>
        <Alert severity="warning">{t('users.form.selectOrganization')}</Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box>
        <Typography color="text.secondary">{t('users.form.loading')}</Typography>
      </Box>
    );
  }

  const pageTitle = isNew ? t('users.form.addTitle') : t('users.form.editTitle');
  const subtitle = isNew
    ? t('users.form.addSubtitle')
    : selectedUser
      ? inferRoleLabel(selectedUser, orgId, roleOrgType)
      : '';

  return (
    <Box sx={{ maxWidth: 1100, mx: 'auto' }}>
      <Box
        sx={{
          mb: 3,
          p: { xs: 2, sm: 2.5 },
          borderRadius: '16px',
          border: (theme) =>
            `1px solid ${alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.22 : 0.12)}`,
          background: (theme) =>
            theme.palette.mode === 'dark'
              ? `linear-gradient(165deg, ${alpha(theme.palette.primary.main, 0.22)} 0%, ${alpha(theme.palette.primary.main, 0.05)} 55%, transparent 100%)`
              : `linear-gradient(165deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.primary.main, 0.03)} 50%, ${theme.palette.background.paper} 100%)`,
          boxShadow: (theme) =>
            theme.palette.mode === 'dark'
              ? `0 10px 28px ${alpha('#000', 0.3)}`
              : `0 10px 28px ${alpha('#0f172a', 0.06)}`,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
          flexWrap="wrap"
          useFlexGap
        >
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ minWidth: 0 }}>
            <IconButton
              onClick={() => {
                if (showUserFormTour) finishTeamTour();
                navigate('/users');
              }}
              aria-label={t('users.form.backToUsers')}
              sx={{
                color: 'text.secondary',
                bgcolor: (theme) =>
                  alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.2 : 0.04),
                '&:hover': {
                  bgcolor: (theme) =>
                    alpha(theme.palette.common.black, theme.palette.mode === 'dark' ? 0.32 : 0.08),
                },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <PageTitleIcon
              icon={isNew ? <PersonAddAlt1OutlinedIcon /> : <EditOutlinedIcon />}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography
                component="h1"
                sx={{
                  fontFamily: CONTENT_FONT_FAMILY_VAR,
                  fontWeight: 800,
                  fontSize: { xs: '1.35rem', sm: '1.6rem' },
                  letterSpacing: '-0.02em',
                  lineHeight: 1.2,
                  color: 'text.primary',
                }}
              >
                {pageTitle}
              </Typography>
              {subtitle ? (
                <Typography
                  sx={{
                    fontFamily: CONTENT_FONT_FAMILY_VAR,
                    mt: 0.35,
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: 'text.secondary',
                    lineHeight: 1.4,
                  }}
                >
                  {!isNew && formData.username
                    ? `${formData.username} · ${subtitle}`
                    : subtitle}
                </Typography>
              ) : null}
            </Box>
          </Stack>
          <SetupCoachmark
            open={formTourPhase === 'save'}
            title={t('selfSetup.tour.userFormSaveTitle')}
            subtitle={t('selfSetup.tour.userFormSaveSubtitle')}
            onDismiss={finishTeamTour}
            onNext={advanceFormTour}
            nextLabel={t('selfSetup.tour.userFormDone')}
          >
            <Button
              id="self-setup-form-save"
              variant="contained"
              size="medium"
              startIcon={<SaveOutlinedIcon />}
              disabled={!canWrite || isSubmitting}
              onClick={handleSubmit}
              sx={{
                textTransform: 'none',
                borderRadius: '12px',
                px: 2.5,
                py: 1,
                fontWeight: 700,
                whiteSpace: 'nowrap',
                boxShadow: (theme) =>
                  `0 8px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
              }}
            >
              {isSubmitting ? t('users.form.saving') : t('users.form.save')}
            </Button>
          </SetupCoachmark>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <SetupCoachmark
        open={formTourPhase === 'account'}
        title={t('selfSetup.tour.userFormAccountTitle')}
        subtitle={t('selfSetup.tour.userFormAccountSubtitle')}
        onDismiss={finishTeamTour}
        onNext={advanceFormTour}
        nextLabel={t('selfSetup.tourNext')}
        fullWidth
      >
        <Box id="self-setup-form-account" sx={{ width: '100%' }}>
          <SectionCard
            icon={<PersonOutlineIcon fontSize="small" />}
            title={t('users.form.accountTitle')}
            subtitle={t('users.form.accountSubtitle')}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('users.form.email')}
                  type="email"
                  fullWidth
                  size="small"
                  value={formData.username}
                  onChange={(e) => setFormData((f) => ({ ...f, username: e.target.value }))}
                  required
                  disabled={!canWrite}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label={t('users.form.phoneSaudi')}
                  fullWidth
                  size="small"
                  value={formData.phone ?? ''}
                  onChange={(e) => setFormData((f) => ({ ...f, phone: e.target.value || null }))}
                  disabled={!canWrite}
                  placeholder="+966512345678"
                />
              </Grid>
              {!isNew && isPlatformAdmin && (
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{t('users.form.status')}</InputLabel>
                <Select
                  value={formData.status}
                  label={t('users.form.status')}
                  disabled={!canWrite}
                  onChange={(e) =>
                    setFormData((f) => ({
                      ...f,
                      status: e.target.value as 'active' | 'inactive',
                    }))
                  }
                >
                  <MenuItem value="active">{t('users.form.active')}</MenuItem>
                  <MenuItem value="inactive">{t('users.form.inactive')}</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          )}
        </Grid>
      </SectionCard>
        </Box>
      </SetupCoachmark>

      {isNew && (
        <SetupCoachmark
          open={formTourPhase === 'password'}
          title={t('selfSetup.tour.userFormPasswordTitle')}
          subtitle={t('selfSetup.tour.userFormPasswordSubtitle')}
          onDismiss={finishTeamTour}
          onNext={advanceFormTour}
          nextLabel={t('selfSetup.tourNext')}
          fullWidth
        >
          <Box id="self-setup-form-password" sx={{ width: '100%' }}>
            <PasswordSetupSection
              password={formData.password}
              passwordConfirm={formData.password_confirm}
              onPasswordChange={(value) => setFormData((f) => ({ ...f, password: value }))}
              onPasswordConfirmChange={(value) =>
                setFormData((f) => ({ ...f, password_confirm: value }))
              }
              disabled={!canWrite}
              onSuggested={() =>
                setSnackbar({ open: true, message: t('users.form.passwordGenerated') })
              }
            />
          </Box>
        </SetupCoachmark>
      )}

      <SetupCoachmark
        open={formTourPhase === 'personal'}
        title={t('selfSetup.tour.userFormPersonalTitle')}
        subtitle={t('selfSetup.tour.userFormPersonalSubtitle')}
        onDismiss={finishTeamTour}
        onNext={advanceFormTour}
        nextLabel={t('selfSetup.tourNext')}
        fullWidth
      >
        <Box id="self-setup-form-personal" sx={{ width: '100%' }}>
      <SectionCard
        icon={<BadgeOutlinedIcon fontSize="small" />}
        title={t('users.form.personalTitle')}
        subtitle={t('users.form.personalSubtitle')}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('users.form.firstName')}
              fullWidth
              size="small"
              value={formData.first_name ?? ''}
              disabled={!canWrite}
              onChange={(e) => setFormData((f) => ({ ...f, first_name: e.target.value || null }))}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('users.form.lastName')}
              fullWidth
              size="small"
              value={formData.last_name ?? ''}
              disabled={!canWrite}
              onChange={(e) => setFormData((f) => ({ ...f, last_name: e.target.value || null }))}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small" required>
              <InputLabel>{t('users.form.gender')}</InputLabel>
              <Select
                value={formData.gender ?? ''}
                label={t('users.form.gender')}
                disabled={!canWrite}
                onChange={(e) => setFormData((f) => ({ ...f, gender: e.target.value }))}
              >
                {GENDER_OPTIONS.map((g) => (
                  <MenuItem key={g} value={g}>
                    {g === 'Male' ? t('users.form.genderMale') : t('users.form.genderFemale')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('users.form.employeeLevel')}</InputLabel>
              <Select
                value={formData.employee_level ?? ''}
                label={t('users.form.employeeLevel')}
                disabled={!canWrite}
                onChange={(e) =>
                  setFormData((f) => ({ ...f, employee_level: e.target.value || null }))
                }
              >
                <MenuItem value="">
                  <em>{t('users.form.none')}</em>
                </MenuItem>
                {EMPLOYEE_LEVEL_OPTIONS.map((level) => (
                  <MenuItem key={level} value={level}>
                    {level === 'Senior'
                      ? t('users.form.levelSenior')
                      : level === 'Mid'
                        ? t('users.form.levelMid')
                        : t('users.form.levelLine')}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('users.form.nationality')}</InputLabel>
              <Select
                value={formData.nationality ?? ''}
                label={t('users.form.nationality')}
                disabled={!canWrite}
                onChange={(e) =>
                  setFormData((f) => ({ ...f, nationality: e.target.value || null }))
                }
              >
                <MenuItem value="">
                  <em>{t('users.form.none')}</em>
                </MenuItem>
                {NATIONALITY_OPTIONS.map((nat) => (
                  <MenuItem key={nat} value={nat}>
                    {nat}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label={t('users.form.dateOfBirth')}
              type="date"
              fullWidth
              size="small"
              disabled={!canWrite}
              value={formData.date_of_birth ?? ''}
              onChange={(e) =>
                setFormData((f) => ({ ...f, date_of_birth: e.target.value || null }))
              }
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label={t('users.form.ibanOptional')}
              fullWidth
              size="small"
              disabled={!canWrite}
              value={formData.iban ?? ''}
              onChange={(e) => setFormData((f) => ({ ...f, iban: e.target.value || null }))}
            />
          </Grid>
        </Grid>
      </SectionCard>
        </Box>
      </SetupCoachmark>

      {isTruckOrg && (
        <SectionCard
          icon={<LocalShippingOutlinedIcon fontSize="small" />}
          title={t('users.form.driverTitle')}
          subtitle={t('users.form.driverSubtitle')}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('users.form.driverLicenceExpiry')}
                type="date"
                fullWidth
                size="small"
                disabled={!canWrite}
                value={formData.driver_license_expiry ?? ''}
                onChange={(e) =>
                  setFormData((f) => ({
                    ...f,
                    driver_license_expiry: e.target.value || null,
                  }))
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('users.form.driverLicenceNumber')}
                fullWidth
                size="small"
                disabled={!canWrite}
                value={formData.driver_license_number ?? ''}
                onChange={(e) =>
                  setFormData((f) => ({
                    ...f,
                    driver_license_number: e.target.value || null,
                  }))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t('users.form.vehicleRegistration')}
                fullWidth
                size="small"
                disabled={!canWrite}
                value={formData.vehicle_registration_number ?? ''}
                onChange={(e) =>
                  setFormData((f) => ({
                    ...f,
                    vehicle_registration_number: e.target.value || null,
                  }))
                }
              />
            </Grid>
          </Grid>
        </SectionCard>
      )}

      {isSiteOpsOrg && (
        <SectionCard
          icon={<StorefrontOutlinedIcon fontSize="small" />}
          title={t('users.form.sitesTitle')}
          subtitle={t('users.form.sitesSubtitle')}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.5}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            sx={{ mb: 2 }}
          >
            <TextField
              size="small"
              fullWidth
              placeholder={t('users.form.searchSites')}
              value={siteSearch}
              onChange={(e) => setSiteSearch(e.target.value)}
              disabled={branchSitesLoading || !canEditSites || allBranchSites}
              sx={{ maxWidth: { sm: 400 }, flex: 1 }}
            />
            <Button
              variant={allBranchSites ? 'contained' : 'outlined'}
              size="small"
              startIcon={<StorefrontOutlinedIcon />}
              disabled={!canEditSites}
              onClick={() => {
                if (allBranchSites) {
                  setAllBranchSites(false);
                } else {
                  setAllBranchSites(true);
                  setSelectedBranchSiteIds([]);
                }
              }}
              sx={{
                whiteSpace: 'nowrap',
                flexShrink: 0,
                textTransform: 'none',
                px: 2,
                py: 0.875,
              }}
            >
              {t('users.form.allCollectionSites')}
            </Button>
          </Stack>

          {allBranchSites ? (
            <Typography variant="body2" color="text.secondary">
              {t('users.form.allSitesHint')}
            </Typography>
          ) : branchSitesLoading ? (
            <Typography variant="body2" color="text.secondary">
              {t('users.form.loadingSites')}
            </Typography>
          ) : branchSitesList.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('users.form.noSites')}
            </Typography>
          ) : (
            <Grid container spacing={2}>
              {branchSitesList
                .filter((site) => {
                  const q = siteSearch.trim().toLowerCase();
                  if (!q) return true;
                  return collectionSiteLabel(site).toLowerCase().includes(q);
                })
                .map((site) => {
                  const selected = selectedBranchSiteIds.includes(site.id);
                  const siteName = collectionSiteLabel(site);
                  const status =
                    typeof site.status === 'string' && site.status.trim()
                      ? site.status
                      : null;
                  return (
                    <Grid item xs={12} sm={6} md={4} key={site.id}>
                      <Card
                        variant="outlined"
                        onClick={() => {
                          if (!canEditSites) return;
                          setAllBranchSites(false);
                          setSelectedBranchSiteIds((prev) =>
                            prev.includes(site.id)
                              ? prev.filter((id) => id !== site.id)
                              : [...prev, site.id],
                          );
                        }}
                        sx={{
                          cursor: canEditSites ? 'pointer' : 'default',
                          height: '100%',
                          borderColor: selected ? 'primary.main' : 'divider',
                          borderWidth: selected ? 2 : 1,
                          bgcolor: selected ? 'action.selected' : 'background.paper',
                          transition: 'border-color 0.2s, background-color 0.2s',
                          opacity: canEditSites ? 1 : 0.6,
                        }}
                      >
                        <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                          <Stack direction="row" spacing={1.5} alignItems="flex-start">
                            <Box
                              sx={{
                                width: 36,
                                height: 36,
                                borderRadius: 1.5,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: selected ? 'primary.main' : 'action.hover',
                                color: selected ? 'primary.contrastText' : 'text.secondary',
                                flexShrink: 0,
                              }}
                            >
                              <LocationOnOutlinedIcon fontSize="small" />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Stack
                                direction="row"
                                alignItems="flex-start"
                                justifyContent="space-between"
                                spacing={1}
                              >
                                <Typography variant="subtitle2" fontWeight={600} noWrap>
                                  {siteName}
                                </Typography>
                                {selected ? (
                                  <CheckCircleIcon color="primary" fontSize="small" />
                                ) : (
                                  <RadioButtonUncheckedIcon color="disabled" fontSize="small" />
                                )}
                              </Stack>
                              {status && (
                                <Chip
                                  size="small"
                                  label={status}
                                  sx={{ mt: 0.75, height: 22, fontSize: '0.7rem' }}
                                />
                              )}
                            </Box>
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
            </Grid>
          )}
          {!allBranchSites && selectedBranchSiteIds.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1.5, display: 'block' }}>
              {t('users.form.sitesSelected', { count: selectedBranchSiteIds.length })}
            </Typography>
          )}
        </SectionCard>
      )}

      <SetupCoachmark
        open={formTourPhase === 'role'}
        title={t('selfSetup.tour.userFormRoleTitle')}
        subtitle={t('selfSetup.tour.userFormRoleSubtitle')}
        onDismiss={finishTeamTour}
        onNext={advanceFormTour}
        nextLabel={t('selfSetup.tourNext')}
        fullWidth
      >
        <Box id="self-setup-form-role" sx={{ width: '100%' }}>
      <SectionCard
        icon={<SecurityOutlinedIcon fontSize="small" />}
        title={t('users.form.roleTitle')}
        subtitle={t('users.form.roleSubtitle')}
        headerAction={
          <Button
            size="small"
            variant="outlined"
            startIcon={<MenuBookOutlinedIcon />}
            onClick={() => setPermissionsRefOpen(true)}
            sx={{
              textTransform: 'none',
              flexShrink: 0,
              borderRadius: '10px',
              fontWeight: 600,
            }}
          >
            {t('users.form.permissionReference')}
          </Button>
        }
      >
        {!advancedOpen && (
          <RadioGroup
            value={selectedCanonicalRole}
            onChange={(e) => {
              setSelectedCanonicalRole(e.target.value as CanonicalRole);
              setAdvancedPermissions([]);
            }}
          >
            <Grid container spacing={1}>
              {grantablePresetRoles.map(({ value, label }) => (
                <Grid item xs={12} sm={6} key={value}>
                  <Card
                    variant="outlined"
                    sx={{
                      cursor: canWrite ? 'pointer' : 'default',
                      borderColor:
                        selectedCanonicalRole === value ? 'primary.main' : 'divider',
                      bgcolor:
                        selectedCanonicalRole === value ? 'action.selected' : 'transparent',
                    }}
                    onClick={() => {
                      if (canWrite) setSelectedCanonicalRole(value);
                    }}
                  >
                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <FormControlLabel
                        value={value}
                        control={<Radio size="small" disabled={!canWrite} />}
                        label={label}
                        sx={{ m: 0, width: '100%' }}
                      />
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </RadioGroup>
        )}

        <FormControlLabel
          sx={{ mt: 2 }}
          control={
            <Checkbox
              checked={advancedOpen}
              disabled={!canWrite}
              onChange={(_, checked) => {
                setAdvancedOpen(checked);
                if (checked) {
                  setSelectedCanonicalRole('');
                  if (selectedCanonicalRole && roleOrgType) {
                    const expanded = expandRoleForSites(
                      roleOrgType,
                      selectedCanonicalRole,
                      orgId,
                      siteIdsForRole,
                    );
                    setAdvancedPermissions(
                      isSiteOpsOrg
                        ? normalizePermissionsForEditor(expanded, orgId, siteIdsForRole)
                        : expanded,
                    );
                  }
                } else {
                  setAdvancedPermissions([]);
                }
              }}
            />
          }
          label={t('users.form.advancedPermissions')}
        />

        <Collapse in={advancedOpen}>
          <Box sx={{ mt: 2 }}>
            <AdvancedPermissionsEditor
              orgId={orgId}
              orgType={roleOrgType}
              siteIds={isSiteOpsOrg ? siteIdsForRole : '*'}
              selected={advancedPermissions}
              onChange={setAdvancedPermissions}
              requesterPerms={permissionList}
              tokenPayload={tokenPayload}
              disabled={!canWrite}
            />
          </Box>
        </Collapse>
      </SectionCard>
        </Box>
      </SetupCoachmark>

      <PermissionsReferenceDialog
        open={permissionsRefOpen}
        onClose={() => setPermissionsRefOpen(false)}
        orgType={roleOrgType}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ open: false, message: '' })}
        message={snackbar.message}
      />
    </Box>
  );
};

export default UserEdit;
